import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Plus, AlertTriangle, Lock, Pencil } from 'lucide-react';
import { getNotes, addNote, updateNote, type IpdProgressNote } from '@/lib/ipdApi';
import { format, isToday, isYesterday } from 'date-fns';
import { useAuth } from '@/context/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dateLabel(iso: string): string {
  const d = new Date(iso);
  if (isToday(d))     return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'dd MMM yyyy');
}

/** Group notes by calendar date (noted_at) — each group is one day */
function groupByDate(notes: IpdProgressNote[]): { date: string; notes: IpdProgressNote[] }[] {
  const map = new Map<string, IpdProgressNote[]>();
  for (const n of notes) {
    const key = format(new Date(n.noted_at), 'yyyy-MM-dd');
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(n);
  }
  return Array.from(map.entries()).map(([date, notes]) => ({ date, notes }));
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  admissionId: string;
  isActive?: boolean;
  admissionDate?: string | null;
  dischargeDate?: string | null;
}

export default function IPDProgressNotesTab({ admissionId, isActive = true, admissionDate, dischargeDate }: Props) {
  const { currentUser } = useAuth();
  const minNotedAt = admissionDate ? format(new Date(admissionDate), "yyyy-MM-dd'T'HH:mm") : undefined;
  const maxNotedAt = format(dischargeDate ? new Date(dischargeDate) : new Date(), "yyyy-MM-dd'T'HH:mm");
  const [notes,   setNotes]   = useState<IpdProgressNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [open,    setOpen]    = useState(false);
  const [text,    setText]    = useState('');
  const [notedAt, setNotedAt] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  // edit state
  const [editingId,   setEditingId]   = useState<string | null>(null);
  const [editText,    setEditText]    = useState('');
  const [editSaving,  setEditSaving]  = useState(false);
  const [editError,   setEditError]   = useState('');

  // earlier wordings shown for these notes
  const [historyOpen, setHistoryOpen] = useState<Set<string>>(new Set());
  const toggleHistory = (id: string) => setHistoryOpen(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // date filter
  const [filterDate, setFilterDate] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNotes(admissionId, filterDate || undefined);
      setNotes(data ?? []);
    } catch (e) {
      console.error('Failed to load notes:', e);
    } finally {
      setLoading(false);
    }
  }, [admissionId, filterDate]);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    setText('');
    setNotedAt(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setError('');
    setOpen(true);
  };

  const handleSave = async () => {
    setError('');
    if (!text.trim()) { setError('Note text is required.'); return; }

    const nowStr = format(new Date(), "yyyy-MM-dd'T'HH:mm");
    const effectiveMax = dischargeDate ? format(new Date(dischargeDate), "yyyy-MM-dd'T'HH:mm") : nowStr;
    if (minNotedAt && notedAt < minNotedAt) {
      setError('Note time cannot be before the admission date.'); return;
    }
    if (notedAt > effectiveMax) {
      setError(dischargeDate ? 'Note time cannot be after the discharge date.' : 'Note time cannot be in the future.'); return;
    }

    try {
      setSaving(true);
      await addNote(admissionId, {
        note_text: text.trim(),
        noted_at:  notedAt ? new Date(notedAt).toISOString() : undefined,
      });
      setOpen(false);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (n: IpdProgressNote) => {
    setEditingId(n.id);
    setEditText(n.note_text);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setEditError('');
  };

  const saveEdit = async (noteId: string) => {
    setEditError('');
    if (!editText.trim()) { setEditError('Note text is required.'); return; }
    try {
      setEditSaving(true);
      await updateNote(admissionId, noteId, editText.trim());
      setEditingId(null);
      await load();
    } catch (e: any) {
      setEditError(e.message || 'Failed to save note.');
    } finally {
      setEditSaving(false);
    }
  };

  const groups = groupByDate(notes);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            className="h-8 text-sm w-36"
          />
          {filterDate && (
            <button
              onClick={() => setFilterDate('')}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
        </div>
        {isActive && (
          <Button size="sm" onClick={openModal}>
            <Plus className="w-4 h-4 mr-1" /> Add note
          </Button>
        )}
      </div>

      {/* Amendment notice */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="w-3 h-3" />
        Notes can't be deleted. Authors can amend their own notes; the earlier wording is kept.
      </div>

      {/* Notes */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">
          {filterDate ? 'No notes for this date.' : 'No progress notes recorded yet.'}
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(({ date, notes: dayNotes }) => (
            <div key={date}>
              {/* Day header */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[13px] font-semibold text-foreground">
                  {dateLabel(dayNotes[0].noted_at)}
                </span>
                <div className="flex-1 border-t border-border/50" />
              </div>

              {/* Notes for this day */}
              <div className="space-y-2">
                {dayNotes.map(n => (
                  <div
                    key={n.id}
                    className="rounded-md border bg-card p-3 space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-foreground">
                        {n.noted_by_name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(n.noted_at), 'h:mm a')}
                        </span>
                        {isActive && editingId !== n.id && n.noted_by === currentUser?.id && (
                          <button
                            onClick={() => startEdit(n)}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="Amend note"
                            title="Amend note"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingId === n.id ? (
                      <div className="space-y-2">
                        <textarea
                          autoFocus
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          rows={4}
                          className={cn(
                            'w-full rounded-md border border-field bg-background px-3 py-2',
                            'text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring',
                          )}
                        />
                        {editError && (
                          <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            {editError}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Button size="sm" onClick={() => saveEdit(n.id)} disabled={editSaving}>
                            {editSaving ? 'Saving…' : 'Save'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit} disabled={editSaving}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className={cn(
                          'text-sm text-foreground whitespace-pre-wrap leading-relaxed',
                        )}>
                          {n.note_text}
                        </p>
                        {n.edited_at && (
                          <p className="text-xs text-muted-foreground italic">
                            Amended by {n.edited_by_name} at {format(new Date(n.edited_at), 'dd MMM yyyy, h:mm a')}
                            {!!n.versions?.length && (
                              <>
                                {' · '}
                                <button type="button" onClick={() => toggleHistory(n.id)}
                                  className="not-italic underline underline-offset-2 hover:text-foreground" aria-expanded={historyOpen.has(n.id)}>
                                  {historyOpen.has(n.id) ? 'Hide earlier wording' : `Show earlier wording${n.versions.length > 1 ? ` (${n.versions.length})` : ''}`}
                                </button>
                              </>
                            )}
                          </p>
                        )}
                        {historyOpen.has(n.id) && (
                          <ol className="mt-1 space-y-1.5 border-l-2 border-divider pl-3">
                            {n.versions?.map((v, i) => (
                              <li key={i} className="text-xs text-muted-foreground">
                                <span className="block">
                                  {v.written_by_name || 'Unknown'}{v.written_at && <> · {format(new Date(v.written_at), 'dd MMM yyyy, h:mm a')}</>}
                                </span>
                                <span className="block whitespace-pre-wrap text-foreground/80 line-through decoration-muted-foreground/50">{v.note_text}</span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Note Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add progress note</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {/* Noted at */}
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">Noted at</label>
              <Input
                type="datetime-local"
                value={notedAt}
                min={minNotedAt}
                max={maxNotedAt}
                onChange={e => setNotedAt(e.target.value)}
                className="h-8 text-sm"
              />
            </div>

            {/* Note text */}
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">Note</label>
              <textarea
                autoFocus
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Enter progress note…"
                rows={6}
                className={cn(
                  'w-full rounded-md border border-field bg-background px-3 py-2',
                  'text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring',
                  'placeholder:text-muted-foreground',
                )}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}

            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Notes can't be deleted. You can amend your own note later; the earlier wording is kept.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
